import React, { useState, useCallback, Fragment } from 'react';
import { useDropzone } from 'react-dropzone';

import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';

import uploadFile from '../util/upload';
import { getAcceptedExtensions } from '../util/acceptedFormats.js';

const clampPercent = (value) => {
	if (!Number.isFinite(value)) return 0;
	return Math.min(100, Math.max(0, value));
};

const toDisplayPercent = (value) => {
	// Clamp and always round up to an integer.
	return Math.ceil(clampPercent(value));
};

const SingleFileUpload = ({
	onFileUploaded,
	uploadUrl,
	disabled,
	text = {},
	existingFile = null,
	accept = {
		'application/pdf': ['.pdf'],
		'image/png': ['.png'],
		'image/jpeg': ['.jpeg', '.jpg'],
	},
	fileType = 'generic',
	context = {},
	allowedDocumentTypes = [],
	uploadZone = 'single',
}) => {
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [uploadError, setUploadError] = useState(null);

	const onDrop = useCallback(async (acceptedFiles) => {
		if (acceptedFiles.length === 0) return;

		const file = acceptedFiles[0]; // Only take the first file

		setUploading(true);
		setUploadProgress(0);
		setUploadError(null);

		try {
			const arrayBuffer = await file.arrayBuffer();
			const uploadedFile = await uploadFile(uploadUrl, arrayBuffer, false, {
				progressHandler: (progress) => setUploadProgress(clampPercent(progress)),
				contentType: file.type,
				filename: file.name,
				type: fileType,
				context,
				allowedDocumentTypes,
			});

			const successfulFile = {
				...uploadedFile,
				fileName: file.name,
				fileType: file.type,
				processingFeedback: uploadedFile.metadata?.validityFeedback || 'Document uploaded successfully',
				validity: uploadedFile.metadata?.validity !== undefined ? uploadedFile.metadata.validity : true,
				uploadZone,
			};

			onFileUploaded(successfulFile);
			setUploading(false);
			setUploadProgress(0);

		} catch (error) {
			console.error('Upload error for file:', file.name, error);

			// Determine error message based on error type
			let errorMessage = 'Upload failed';
			if (error.message?.includes('401')) {
				errorMessage = 'Unauthorized - please check your permissions';
			} else if (error.message?.includes('403')) {
				errorMessage = 'Forbidden - access denied';
			} else if (error.message?.includes('404')) {
				errorMessage = 'Upload endpoint not found';
			} else if (error.message?.includes('500')) {
				errorMessage = 'Server error - please try again';
			} else if (error.message?.includes('Network')) {
				errorMessage = 'Network error - please check your connection';
			} else if (error.message) {
				errorMessage = error.message;
			}

			setUploadError(errorMessage);
			setUploading(false);
			setUploadProgress(0);
		}
	}, [uploadUrl, onFileUploaded, fileType, context, allowedDocumentTypes, uploadZone]);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		disabled: disabled || uploading,
		accept,
		multiple: false, // Only allow single file
	});

	const acceptedExtensions = getAcceptedExtensions(accept);

	const removeFile = () => {
		onFileUploaded(null);
		setUploadError(null);
	};

	const dismissError = () => {
		setUploadError(null);
	};

	// Show existing file
	if (existingFile && !uploading && !uploadError) {
		return (
			<Card
				variant="outlined"
				sx={{
					border: '2px dashed #ccc',
				}}
			>
				<ListItem sx={{ height: 100 }}>
					<ListItemIcon>
						<CheckIcon color={existingFile.validity === false ? 'error' : 'success'} />
					</ListItemIcon>
					<ListItemText
						primary={existingFile.fileName || existingFile.name}
						secondary={
							<Fragment>
								{existingFile.processingFeedback && (
									<Typography
										variant='body2'
										color={existingFile.validity === false ? 'error' : 'text.secondary'}
									>
										{existingFile.processingFeedback}
									</Typography>
								)}
							</Fragment>
						}
					/>
					<Button
						startIcon={<DeleteIcon />}
						onClick={removeFile}
						disabled={disabled}
						color="error"
						size="small"
					>
						{text.removeDocument || 'Remove'}
					</Button>
				</ListItem>
			</Card>
		);
	}

	// Show uploading state
	if (uploading) {
		const percent = toDisplayPercent(uploadProgress);
		return (
			<Card variant="outlined" sx={{ border: '2px solid #ccc' }}>
				<ListItem sx={{ height: 100 }}>
					<ListItemIcon>
						<CircularProgress size={24} variant="determinate" value={percent} />
					</ListItemIcon>
					<ListItemText
						primary="Uploading..."
						secondary={`${percent}%`}
					/>
				</ListItem>
			</Card>
		);
	}

	// Show error state
	if (uploadError) {
		return (
			<Card variant="outlined" sx={{ border: '2px dashed #ccc'}}>
				<ListItem sx={{ height: 100 }}>
					<ListItemIcon>
						<ErrorIcon color="error" />
					</ListItemIcon>
					<ListItemText
						primary="Upload failed"
						secondary={
							<Typography variant='body2' color="error">
								{uploadError}
							</Typography>
						}
					/>
					<Button
						onClick={dismissError}
						disabled={disabled}
						color="error"
						size="small"
					>
						Dismiss
					</Button>
				</ListItem>
			</Card>
		);
	}

	// Show dropzone (empty state)
	return (
		<Card
			variant="outlined"
			sx={{
				border: '2px dashed #ccc',
				backgroundColor: isDragActive ? '#f0f0f0' : '#fafafa',
				cursor: disabled ? 'not-allowed' : 'pointer',
				'&:hover': disabled ? {} : { backgroundColor: '#f0f0f0' },
			}}
			{...getRootProps()}
		>
			<input {...getInputProps()} />
			<ListItem sx={{ height: 100 }}>
				<ListItemIcon>
					<UploadIcon />
				</ListItemIcon>
				<ListItemText
					primary={isDragActive ? 'Drop file here...' : 'Upload Document'}
					secondary={`Drag and drop a file here, or click to select a file ${acceptedExtensions.length > 0 ? `(${acceptedExtensions.join(', ')})` : ''}`}
				/>
			</ListItem>
		</Card>
	);
};

SingleFileUpload.type = 'SingleFileUpload';
SingleFileUpload.spec = [{
	name: 'SingleFileUpload',
	key: 'SingleFileUpload',
	title: 'Single file upload with drag and drop',
	allowedFields: [],
	fields: [
		{
			type: 'TextField',
			name: 'label',
			label: 'Label',
		},
		{
			type: 'TextField',
			name: 'uploadUrl',
			label: 'Upload URL',
		},
		{
			type: 'TextField',
			name: 'fileType',
			label: 'File type',
		},
	],
	defaultProperties: {
		uploadUrl: '/api/files',
		fileType: 'generic',
	},
}];

export default SingleFileUpload;
